import React, { useState, Children, Fragment } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PhoneIcon, CloudIcon, RadioIcon, ServerIcon, PhoneCallIcon, PhoneForwardedIcon, ArrowRightIcon, SmartphoneIcon, UsersIcon, SettingsIcon, ClipboardListIcon, LayoutIcon, TruckIcon, GraduationCapIcon, HomeIcon, StethoscopeIcon, ScaleIcon, CheckIcon, XIcon, ArrowRightLeftIcon, HelpCircleIcon, ChevronDownIcon, CheckCircleIcon, MessageSquareIcon, VideoIcon, BotIcon, FileTextIcon, MicIcon } from 'lucide-react';
import { PricingSection } from '../components/PricingSection';
const voiceServices = [{
  icon: CloudIcon,
  title: 'UCaaS',
  description: 'The ultimate unified platform integrating voice, HD video, professional text messaging, and AI-driven collaboration. UCaaS transforms your communication with intelligent features like automated note-taking, searchable transcripts, and an AI receptionist, providing a seamless experience across all devices.',
  color: 'text-indigo-500',
  bg: 'bg-indigo-50',
  id: 'ucaas'
}, {
  icon: PhoneCallIcon,
  title: 'VoIP / Hosted Voice',
  description: 'Move beyond traditional phone lines with our VoIP Services. Experience high-definition voice calls over the internet, reducing costs and increasing flexibility.',
  color: 'text-blue-500',
  bg: 'bg-blue-50',
  id: 'business-phone'
}, {
  icon: RadioIcon,
  title: 'SIP Trunks',
  description: 'SIP Trunks are a perfect way to leverage the latest VoIP technologies, including business continuity features that ensure you never miss a call, even in a disaster.',
  color: 'text-teal-500',
  bg: 'bg-teal-50',
  id: 'sip-trunks'
}, {
  icon: ServerIcon,
  title: 'PRI Trunks',
  description: 'For businesses that require reliable, high-capacity voice communications, PRI Trunks offer robust connections and enhanced security.',
  color: 'text-purple-500',
  bg: 'bg-purple-50',
  id: 'pri-trunks'
}, {
  icon: PhoneForwardedIcon,
  title: 'Digital Phone Lines',
  description: "Get dedicated phone lines with advanced features such as call forwarding, voicemail, and caller ID. Enhance your team's communication and maintain a professional image.",
  color: 'text-amber-500',
  bg: 'bg-amber-50',
  id: 'digital-phone-lines'
}, {
  icon: PhoneIcon,
  title: 'Plain Old Telephone Systems (POTS)',
  description: 'Traditional analog phone service delivered over copper wires. While legacy systems are being phased out, we help businesses manage their transition to modern alternatives or maintain critical analog connections.',
  color: 'text-orange-500',
  bg: 'bg-orange-50',
  id: 'pots'
}, {
  icon: SmartphoneIcon,
  title: 'Wireless Cellphones',
  description: 'Keep your team connected everywhere with enterprise-grade wireless plans and the latest mobile hardware. We provide managed mobile solutions that scale with your business.',
  color: 'text-blue-500',
  bg: 'bg-blue-50',
  id: 'wireless-cellphones'
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4
    }
  }
};
export function BusinessPhonePage({
  onNavigate
}: {
  onNavigate: (page: string) => void;
}) {
  const [activeFaq, setActiveFaq] = useState<number | null>(null);
  return <div data-id="element-917">
      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-24 overflow-hidden bg-slate-900" data-id="element-918">
        <div className="absolute inset-0 bg-grid-pattern opacity-20" data-id="element-919"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-slate-900/90" data-id="element-920"></div>
        <div className="absolute top-0 right-1/3 -translate-y-1/4 w-[600px] h-[600px] bg-emerald-500/12 rounded-full blur-[120px] pointer-events-none" data-id="element-921"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-922">
          <div className="max-w-3xl" data-id="element-923">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} data-id="element-924">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white text-sm font-medium mb-6 backdrop-blur-sm" data-id="element-925">
                <span className="flex h-2 w-2 rounded-full bg-emerald-400 mr-2" data-id="element-926"></span>
                Modern Voice Solutions
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-4 tracking-tight" data-id="element-927">
                POTS Replacement &<br className="hidden md:block" data-id="element-928" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent to-accent-dark" data-id="element-929">
                  Business Phones
                </span>
              </h1>
              <p className="text-lg md:text-xl text-slate-300 leading-relaxed mb-8 max-w-2xl" data-id="element-930">
                At Ambient Telecom, we believe strong communication is key to
                business success. As copper phone lines face rising costs and
                eventual decommissioning, we offer modern, cost-effective
                alternatives to meet your current and future phone service
                needs.
              </p>
              <button onClick={() => onNavigate('home')} className="bg-accent hover:bg-accent-dark text-white px-8 py-4 rounded-full font-bold text-lg transition-colors shadow-lg inline-flex items-center" data-id="element-931">
                Get a Free Consultation
                <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-932" />
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section className="py-24 bg-slate-50" data-id="element-933">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-934">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-935">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-936">
              Our Implementation Process
            </h2>
            <p className="text-lg text-slate-600" data-id="element-937">
              We ensure a smooth transition from your legacy system to a modern,
              powerful voice solution.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8" data-id="element-938">
            {[{
            title: 'Assessment and Recommendation',
            desc: 'We evaluate your current call volume, user requirements, and hardware needs.',
            icon: ClipboardListIcon
          }, {
            title: 'System Design',
            desc: 'We architect a custom call flow and feature set tailored to your business operations.',
            icon: LayoutIcon
          }, {
            title: 'Porting & Setup',
            desc: 'We manage the transfer of your existing numbers and configure your new system.',
            icon: TruckIcon
          }, {
            title: 'Team Training',
            desc: 'We provide comprehensive training to ensure your team is productive from day one.',
            icon: GraduationCapIcon
          }].map((step, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm" data-id="element-939">
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-6" data-id="element-940">
                  <step.icon className="w-6 h-6 text-primary" data-id="element-941" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-942">
                  {step.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed" data-id="element-943">
                  {step.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Cloud-Based Benefits */}
      <section className="py-20 bg-white" data-id="element-944">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-945">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-946">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} data-id="element-947">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6" data-id="element-948">
                Stay connected from anywhere with our cloud-based phone services
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-8" data-id="element-949">
                Empower your team to stay in touch, whether they're at their
                desks or on the go, with seamless call mobility. Employees can
                personalize their features with ease, while administrators
                maintain full control of the system through an intuitive,
                user-friendly portal.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6" data-id="element-950">
                {[{
                icon: SmartphoneIcon,
                label: 'Call Mobility',
                desc: 'Work from any device'
              }, {
                icon: UsersIcon,
                label: 'Team Control',
                desc: 'Personalized features'
              }, {
                icon: SettingsIcon,
                label: 'Easy Admin',
                desc: 'Intuitive portal'
              }].map((item, i) => {
                const Icon = item.icon;
                return <div key={i} className="text-center" data-id="element-951">
                      <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-3" data-id="element-952">
                        <Icon className="w-6 h-6 text-primary" data-id="element-953" />
                      </div>
                      <div className="text-sm font-bold text-slate-900" data-id="element-954">
                        {item.label}
                      </div>
                      <div className="text-xs text-slate-500 mt-1" data-id="element-955">
                        {item.desc}
                      </div>
                    </div>;
              })}
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="relative" data-id="element-956">
              <div className="rounded-2xl overflow-hidden shadow-2xl" data-id="element-957">
                <img src="https://images.unsplash.com/photo-1596524430615-b46475ddff6e?w=800&h=600&fit=crop" alt="Business phone system" className="w-full h-auto object-cover" data-id="element-958" />
                <div className="absolute inset-0 bg-gradient-to-tr from-slate-900/30 to-transparent" data-id="element-959"></div>
              </div>
              {/* Floating badge */}
              <div className="absolute -bottom-5 -left-4 md:left-8 bg-white rounded-2xl shadow-xl p-4 border border-slate-100" data-id="element-960">
                <div className="flex items-center gap-3" data-id="element-961">
                  <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center" data-id="element-962">
                    <PhoneIcon className="w-5 h-5 text-emerald-600" data-id="element-963" />
                  </div>
                  <div data-id="element-964">
                    <div className="text-sm font-bold text-slate-900" data-id="element-965">
                      HD Voice
                    </div>
                    <div className="text-xs text-slate-500" data-id="element-966">
                      Crystal clear calls
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* UCaaS AI Advantage Section */}
      <section className="py-24 bg-slate-50 overflow-hidden" data-id="element-967">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-968">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-969">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} data-id="element-970">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-971">
                The UCaaS AI Advantage
              </h2>
              <p className="text-lg text-slate-600" data-id="element-972">
                Go beyond simple voice calls. Our UCaaS solution leverages
                cutting-edge AI to streamline your workflow and enhance every
                interaction.
              </p>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-973">
            {[{
            title: 'Professional Texting',
            desc: 'Send and receive SMS/MMS messages directly from your business number. Keep your personal number private while staying connected.',
            icon: MessageSquareIcon,
            color: 'text-blue-500',
            bg: 'bg-blue-50'
          }, {
            title: 'HD Video Conferencing',
            desc: 'Host crystal-clear video meetings with screen sharing and collaboration tools built directly into your phone system.',
            icon: VideoIcon,
            color: 'text-purple-500',
            bg: 'bg-purple-50'
          }, {
            title: 'AI Receptionist',
            desc: 'Intelligent automated routing that understands caller intent and directs them to the right department instantly, 24/7.',
            icon: BotIcon,
            color: 'text-emerald-500',
            bg: 'bg-emerald-50'
          }, {
            title: 'Automated Note Taking',
            desc: 'Focus on the conversation while AI generates concise summaries and action items immediately after every call.',
            icon: FileTextIcon,
            color: 'text-amber-500',
            bg: 'bg-amber-50'
          }, {
            title: 'Searchable Transcripts',
            desc: 'Every call is automatically transcribed. Search through past conversations to find specific details or compliance records.',
            icon: MicIcon,
            color: 'text-rose-500',
            bg: 'bg-rose-50'
          }, {
            title: 'Unified Experience',
            desc: 'Switch seamlessly between your desk phone, mobile app, and desktop computer without ever dropping a call.',
            icon: SmartphoneIcon,
            color: 'text-indigo-500',
            bg: 'bg-indigo-50'
          }].map((feature, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm hover:shadow-xl transition-all group" data-id="element-974">
                <div className={`w-14 h-14 ${feature.bg} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`} data-id="element-975">
                  <feature.icon className={`w-7 h-7 ${feature.color}`} data-id="element-976" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-977">
                  {feature.title}
                </h3>
                <p className="text-slate-600 leading-relaxed text-sm" data-id="element-978">
                  {feature.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Feature Comparison */}
      <section className="py-24 bg-slate-900 text-white" data-id="element-979">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-980">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-981">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-982">
              Compare Your Options
            </h2>
            <p className="text-lg text-slate-400" data-id="element-983">
              See how modern cloud communications stack up against legacy
              systems.
            </p>
          </div>

          <div className="overflow-x-auto pb-8" data-id="element-984">
            <div className="min-w-[1200px] grid grid-cols-5 gap-4" data-id="element-985">
              <div className="col-span-1" data-id="element-986"></div>
              <div className="bg-slate-800 rounded-t-2xl p-6 text-center border-b-4 border-slate-600" data-id="element-987">
                <h3 className="font-bold text-xl" data-id="element-988">Wireless & Cellular</h3>
                <p className="text-sm text-slate-400 mt-1" data-id="element-989">
                  Mobile Connectivity
                </p>
              </div>
              <div className="bg-slate-800 rounded-t-2xl p-6 text-center border-b-4 border-slate-600" data-id="element-990">
                <h3 className="font-bold text-xl" data-id="element-991">Legacy POTS</h3>
                <p className="text-sm text-slate-400 mt-1" data-id="element-992">Traditional Lines</p>
              </div>
              <div className="bg-slate-800 rounded-t-2xl p-6 text-center border-b-4 border-blue-500" data-id="element-993">
                <h3 className="font-bold text-xl" data-id="element-994">VoIP</h3>
                <p className="text-sm text-slate-400 mt-1" data-id="element-995">Internet Voice</p>
              </div>
              <div className="bg-primary/20 rounded-t-2xl p-6 text-center border-b-4 border-primary relative" data-id="element-996">
                <div className="absolute -top-1 left-1/2 -translate-x-1/2 bg-primary text-white text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap" data-id="element-997">
                  RECOMMENDED
                </div>
                <h3 className="font-bold text-xl text-white" data-id="element-998">UCaaS</h3>
                <p className="text-sm text-white mt-1" data-id="element-999">Unified Cloud</p>
              </div>

              {[{
              feature: 'Cost Efficiency',
              wireless: 'Moderate (Per Device)',
              pots: 'Low (Rising Rates)',
              voip: 'High (Service Savings)',
              ucaas: 'Best (Total ROI)'
            }, {
              feature: 'Scalability',
              wireless: 'Easy (Add Lines)',
              pots: 'Poor (Physical Wiring)',
              voip: 'Excellent (Digital)',
              ucaas: 'Instant (Cloud-Native)'
            }, {
              feature: 'Remote Access',
              wireless: 'Native Mobile Only',
              pots: 'None (Fixed Location)',
              voip: 'App-Based (Softphone)',
              ucaas: 'Full Unified App'
            }, {
              feature: 'Video/Meetings',
              wireless: 'Basic (FaceTime/SMS)',
              pots: 'Not Supported',
              voip: 'Limited (Add-on)',
              ucaas: 'Integrated HD Video'
            }, {
              feature: 'Team Messaging',
              wireless: 'Standard SMS',
              pots: 'Not Supported',
              voip: 'Basic Chat Only',
              ucaas: 'Enterprise Presence'
            }, {
              feature: 'AI Receptionist',
              wireless: 'Basic Voicemail',
              pots: 'Auto-Attendant Only',
              voip: 'Standard IVR',
              ucaas: 'Intelligent AI Routing'
            }, {
              feature: 'Call Transcripts',
              wireless: 'Not Supported',
              pots: 'Not Supported',
              voip: 'Optional Add-on',
              ucaas: 'Included (Searchable)'
            }, {
              feature: 'AI Note Taking',
              wireless: 'Manual Only',
              pots: 'Manual Only',
              voip: 'Not Supported',
              ucaas: 'Automated Summaries'
            }, {
              feature: 'Advanced Analytics',
              wireless: 'Basic Usage Logs',
              pots: 'None (Paper Bills)',
              voip: 'Digital Call Logs',
              ucaas: 'Deep AI Insights'
            }].map((row, i) => <Fragment key={i} data-id="element-1000">
                  <div className="bg-slate-800/50 p-4 rounded-lg flex items-center font-bold text-white" data-id="element-1001">
                    {row.feature}
                  </div>
                  <div className="bg-slate-800/30 p-4 rounded-lg flex items-center justify-center text-center text-sm text-white" data-id="element-1002">
                    {row.wireless}
                  </div>
                  <div className="bg-slate-800/30 p-4 rounded-lg flex items-center justify-center text-center text-sm text-white italic" data-id="element-1003">
                    {row.pots}
                  </div>
                  <div className="bg-slate-800/30 p-4 rounded-lg flex items-center justify-center text-center text-sm text-white" data-id="element-1004">
                    {row.voip}
                  </div>
                  <div className="bg-primary/10 p-4 rounded-lg flex items-center justify-center text-center text-sm font-bold text-white" data-id="element-1005">
                    {row.ucaas}
                  </div>
                </Fragment>)}
            </div>
          </div>
        </div>
      </section>

      {/* Industry Solutions */}
      <section className="py-24 bg-white" data-id="element-1006">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1007">
          <div className="flex flex-col lg:flex-row items-center gap-16" data-id="element-1008">
            <div className="lg:w-1/2" data-id="element-1009">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6" data-id="element-1010">
                Solutions for Every Sector
              </h2>
              <p className="text-lg text-slate-600 mb-10" data-id="element-1011">
                Communication needs vary by industry. We provide specialized
                configurations for professional environments.
              </p>

              <div className="space-y-6" data-id="element-1012">
                {[{
                title: 'Real Estate',
                desc: 'Mobile-first solutions with advanced call forwarding for agents on the move.',
                icon: HomeIcon
              }, {
                title: 'Medical',
                desc: 'HIPAA-compliant voice services with secure messaging and appointment routing.',
                icon: StethoscopeIcon
              }, {
                title: 'Legal',
                desc: 'Professional call recording and detailed billing reports for client consultations.',
                icon: ScaleIcon
              }].map((industry, i) => <div key={i} className="flex items-start gap-4" data-id="element-1013">
                    <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center shrink-0" data-id="element-1014">
                      <industry.icon className="w-5 h-5 text-slate-600" data-id="element-1015" />
                    </div>
                    <div data-id="element-1016">
                      <h4 className="font-bold text-slate-900" data-id="element-1017">
                        {industry.title}
                      </h4>
                      <p className="text-slate-500 text-sm" data-id="element-1018">{industry.desc}</p>
                    </div>
                  </div>)}
              </div>
            </div>
            <div className="lg:w-1/2" data-id="element-1019">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl bg-slate-100" data-id="element-1020">
                <img src="https://images.unsplash.com/photo-1600880212340-02d956371b27?w=800&h=600&fit=crop" alt="Business professionals collaborating in a modern office" className="w-full h-auto object-cover min-h-[400px]" data-id="element-1021" />
                <div className="absolute inset-0 bg-accent/10 mix-blend-multiply" data-id="element-1022"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Voice Services */}
      <section className="py-20 bg-slate-50" data-id="element-1023">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1024">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1025">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-1026">
              Our Voice Solutions
            </h2>
            <p className="text-lg text-slate-600" data-id="element-1027">
              Advanced voice services providing clear, reliable, and efficient
              solutions tailored to your specific needs.
            </p>
          </div>

          <motion.div className="space-y-6" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-60px'
        }} data-id="element-1028">
            {voiceServices.map((service, i) => {
            const Icon = service.icon;
            const isEven = i % 2 === 0;
            return <motion.button key={i} variants={itemVariants} onClick={() => onNavigate(service.id)} className={`w-full text-left bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden flex flex-col group hover:shadow-xl hover:border-primary/20 transition-all duration-300 ${isEven ? 'md:flex-row' : 'md:flex-row-reverse'}`} data-id="element-1029">
                  <div className="flex-1 p-8 md:p-10 flex flex-col justify-center" data-id="element-1030">
                    <div className={`w-12 h-12 ${service.bg} rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform`} data-id="element-1031">
                      <Icon className={`w-6 h-6 ${service.color}`} data-id="element-1032" />
                    </div>
                    <div className="flex items-center justify-between mb-3" data-id="element-1033">
                      <h3 className="text-2xl font-bold text-slate-900" data-id="element-1034">
                        {service.title}
                      </h3>
                      <ArrowRightIcon className="w-5 h-5 text-slate-300 group-hover:text-primary group-hover:translate-x-1 transition-all" data-id="element-1035" />
                    </div>
                    <p className="text-slate-600 leading-relaxed" data-id="element-1036">
                      {service.description}
                    </p>
                  </div>
                  <div className={`flex-shrink-0 w-full md:w-2 ${service.bg} group-hover:bg-primary transition-colors`} data-id="element-1037"></div>
                </motion.button>;
          })}
          </motion.div>
        </div>
      </section>

      {/* Number Porting Made Easy */}
      <section className="py-24 bg-white" data-id="element-1038">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1039">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1040">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-1041">
              Keep Your Existing Numbers
            </h2>
            <p className="text-lg text-slate-600" data-id="element-1042">
              We make the transition seamless. You won't lose a single call or
              have to update your marketing materials.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative" data-id="element-1043">
            <div className="hidden md:block absolute top-1/2 left-0 w-full h-0.5 bg-slate-100 -translate-y-8 z-0" data-id="element-1044"></div>
            {[{
            icon: ClipboardListIcon,
            title: '1. Submit Request',
            desc: 'Provide us with your current phone numbers and a recent bill.'
          }, {
            icon: ArrowRightLeftIcon,
            title: '2. We Coordinate',
            desc: 'We handle all the paperwork and coordination with your old carrier.'
          }, {
            icon: CheckCircleIcon,
            title: '3. Seamless Cutover',
            desc: 'Your numbers port over to the new system with zero downtime.'
          }].map((step, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className="relative z-10 text-center" data-id="element-1045">
                <div className="w-16 h-16 bg-white border-4 border-slate-50 rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm" data-id="element-1046">
                  <step.icon className="w-8 h-8 text-primary" data-id="element-1047" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-1048">
                  {step.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed" data-id="element-1049">
                  {step.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* POTS Replacement Highlight */}
      <section className="py-16 bg-white" data-id="element-1050">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1051">
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="bg-slate-900 rounded-3xl p-10 md:p-16 relative overflow-hidden" data-id="element-1052">
            <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/15 rounded-full blur-[80px] pointer-events-none" data-id="element-1053"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-teal-500/10 rounded-full blur-[60px] pointer-events-none" data-id="element-1054"></div>
            <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-10 items-center" data-id="element-1055">
              <div data-id="element-1056">
                <div className="text-xs font-bold text-emerald-400 uppercase tracking-widest mb-3" data-id="element-1057">
                  Why Switch?
                </div>
                <h3 className="text-3xl font-bold text-white mb-4" data-id="element-1058">
                  Copper lines are being decommissioned
                </h3>
                <p className="text-slate-300 leading-relaxed" data-id="element-1059">
                  As copper phone lines supporting Plain Old Telephone Service
                  (POTS) face rising costs and eventual decommissioning, now is
                  the time to transition to modern, cost-effective alternatives
                  that deliver better quality and reliability.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-1060">
                {[{
                value: '70%',
                label: 'Cost savings vs POTS'
              }, {
                value: 'HD',
                label: 'Voice quality'
              }, {
                value: '99.99%',
                label: 'Uptime guarantee'
              }, {
                value: '24/7',
                label: 'Support included'
              }].map((stat, i) => <div key={i} className="bg-white/5 rounded-xl p-4 text-center border border-white/10" data-id="element-1061">
                    <div className="text-2xl font-extrabold text-emerald-400" data-id="element-1062">
                      {stat.value}
                    </div>
                    <div className="text-xs text-slate-400 mt-1" data-id="element-1063">
                      {stat.label}
                    </div>
                  </div>)}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pricing Section */}
      <PricingSection category="Telephony" data-id="element-1064" />

      {/* Phone System FAQ */}
      <section className="py-24 bg-white" data-id="element-1065">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1066">
          <div className="text-center mb-16" data-id="element-1067">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 flex items-center justify-center gap-3" data-id="element-1068">
              <HelpCircleIcon className="w-8 h-8 text-primary" data-id="element-1069" />
              Phone System FAQ
            </h2>
            <p className="text-lg text-slate-600" data-id="element-1070">
              Common questions about upgrading your business phone system.
            </p>
          </div>
          <div className="space-y-4" data-id="element-1071">
            {[{
            q: 'Can I keep my current phone numbers?',
            a: 'Absolutely. We port your existing local, toll-free, and fax numbers to your new system seamlessly. Our porting and setup process is carefully managed to ensure you experience zero downtime, keeping your business fully operational throughout the transition.'
          }, {
            q: 'What equipment do I need?',
            a: 'For UCaaS, you can use your existing computers and smartphones with our app. If you prefer physical desk phones, we offer a range of modern IP phones that plug directly into your internet connection.'
          }, {
            q: 'How reliable is VoIP/UCaaS?',
            a: 'Extremely reliable. Our cloud platforms offer 99.99% uptime guarantees. Plus, if your office loses power or internet, calls can automatically route to mobile phones.'
          }, {
            q: 'Can remote workers use the system?',
            a: 'Yes! Remote workers can use the mobile or desktop app to make and receive calls from their business number, just as if they were sitting in the office.'
          }, {
            q: 'What about 911/E911?',
            a: 'Our systems fully support Enhanced 911 (E911). You can register the physical location of each user so emergency services know exactly where to go, even for remote workers.'
          }, {
            q: 'Am I going to lose calls?',
            a: 'No. Our systems are designed for 99.99% uptime and include automatic failover features. If your primary internet connection goes down, calls can be instantly rerouted to mobile devices or other locations, ensuring you never miss an important business communication.'
          }].map((faq, i) => <div key={i} className="border border-slate-200 rounded-2xl overflow-hidden bg-slate-50" data-id="element-1072">
                <button onClick={() => setActiveFaq(activeFaq === i ? null : i)} className="w-full flex items-center justify-between p-6 text-left hover:bg-white transition-colors" data-id="element-1073">
                  <span className="font-bold text-slate-900" data-id="element-1074">{faq.q}</span>
                  <motion.div animate={{
                rotate: activeFaq === i ? 180 : 0
              }} data-id="element-1075">
                    <ChevronDownIcon className="w-5 h-5 text-slate-400" data-id="element-1076" />
                  </motion.div>
                </button>
                <AnimatePresence data-id="element-1077">
                  {activeFaq === i && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} className="overflow-hidden bg-white" data-id="element-1078">
                      <div className="p-6 pt-0 text-slate-600 border-t border-slate-100" data-id="element-1079">
                        {faq.a}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>)}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary relative overflow-hidden py-20" data-id="element-1080">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-1081">
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-white opacity-5 rounded-full blur-3xl" data-id="element-1082"></div>
          <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-white opacity-10 rounded-full blur-3xl" data-id="element-1083"></div>
        </div>

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center z-10" data-id="element-1084">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-1085">
            Elevate Your Business Phone System
          </h2>
          <p className="text-xl text-blue-100 mb-10 max-w-3xl mx-auto leading-relaxed" data-id="element-1086">
            Your business communication deserves the best, and Ambient Telecom
            is here to deliver. Whether you're looking to enhance connectivity,
            improve reliability, or streamline your communication systems — we
            have the tailored voice solutions you've been looking for.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-1087">
            <button onClick={() => onNavigate('home')} className="w-full sm:w-auto bg-white text-primary hover:bg-slate-50 px-8 py-4 rounded-full font-bold text-lg transition-colors shadow-lg inline-flex items-center justify-center" data-id="element-1088">
              Contact Us Today
              <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-1089" />
            </button>
            <a href="tel:+4435451747" className="w-full sm:w-auto bg-transparent border-2 border-white text-white hover:bg-white/10 px-8 py-4 rounded-full font-bold text-lg transition-colors" data-id="element-1090">
              Call Us: (443) 545-1747
            </a>
          </div>
        </div>
      </section>
    </div>;
}